<template>
  <div class="solar-system-loader">
    <div class="sun">
      <div class="loading-text">LOADING</div>
    </div>
    <template v-if="planets.length > 0">
      <div 
        v-for="(planet, index) in planets" 
        :key="index"
        class="orbit"
        :style="{ 
          width: `${planet.orbitSize}px`, 
          height: `${planet.orbitSize}px`,
          animationDuration: `${planet.speed}s`
        }"
      >
        <!-- Spaceship -->
        <div 
          v-if="planet.isSpaceship"
          class="spaceship"
          :style="{ 
            width: `${planet.size}px`,
            height: `${planet.size}px`,
            animationDuration: `${planet.speed}s`
          }"
        >
          <img 
            :src="planet.spaceshipSvg" 
            :alt="planet.spaceshipName"
            class="spaceship-image"
            @error="handleImageError"
            @load="handleImageLoad"
            onerror="this.style.display='none'"
          />
          <!-- Fallback: simple spaceship shape if SVG fails to load -->
          <div 
            v-if="!planet.spaceshipSvg" 
            class="spaceship-fallback"
          ></div>
        </div>
        
        <!-- Regular Planet -->
        <div 
          v-else
          class="planet" 
          :style="{ 
            background: planet.gradient,
            boxShadow: planet.shadow,
            width: `${planet.size}px`,
            height: `${planet.size}px`
          }"
        >
          <div 
            v-if="planet.hasMoon"
            class="moon"
            :style="{ animationDuration: `${planet.moonSpeed}s` }"
          ></div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
// Create computed paths for the SVG assets to ensure proper resolution
const getSvgPath = (filename: string) => {
  // For Firebase hosting, ensure we use the full path
  if (process.client) {
    return `${window.location.origin}/svg/${filename}`;
  }
  return `/svg/${filename}`;
}

interface Planet {
  orbitSize: number;
  size: number;
  speed: number;
  gradient: string;
  shadow: string;
  hasMoon: boolean;
  moonSpeed: number;
  isSpaceship?: boolean;
  spaceshipSvg?: string;
  spaceshipName?: string;
}

// Generate deterministic planets to avoid hydration mismatch
const generatePlanets = (): Planet[] => {
  // Use a fixed seed for consistent generation between SSR and client
  const numPlanets = 5; // Fixed number of planets to avoid randomness
  const planets: Planet[] = [];
  
  const colors = [
    { gradient: 'radial-gradient(circle, #00bfff, #0080ff)', shadow: '0 0 8px #00bfff' },
    { gradient: 'radial-gradient(circle, #ff6b6b, #ff4757)', shadow: '0 0 10px #ff6b6b' },
    { gradient: 'radial-gradient(circle, #2ed573, #1e824c)', shadow: '0 0 8px #2ed573' },
    { gradient: 'radial-gradient(circle, #ffa502, #ff6348)', shadow: '0 0 8px #ffa502' },
    { gradient: 'radial-gradient(circle, #a55eea, #8854d0)', shadow: '0 0 8px #a55eea' }
  ];
  
  const spaceshipSvgs = [
    { svg: 'satellite.svg', name: 'satellite' },
    { svg: 'mars-pathfinder.svg', name: 'mars-pathfinder' },
    { svg: 'defense-satellite.svg', name: 'defense-satellite' },
    { svg: 'rocket.svg', name: 'rocket' },
    { svg: 'starfighter.svg', name: 'starfighter' }
  ];
  
  // Deterministic planet generation
  const planetConfigs = [
    { size: 12, hasMoon: false, moonSpeed: 2.0 },
    { size: 16, hasMoon: true, moonSpeed: 1.8 },
    { size: 14, hasMoon: false, moonSpeed: 2.2 },
    { size: 13, hasMoon: false, moonSpeed: 1.9 },
    { size: 15, hasMoon: true, moonSpeed: 2.1 }
  ];
  
  for (let i = 0; i < numPlanets; i++) {
    const baseOrbitSize = 110 + (i * 50);
    const colorIndex = i % colors.length;
    const config = planetConfigs[i];
    
    planets.push({
      orbitSize: baseOrbitSize,
      size: config.size,
      speed: 3 + (i * 0.8),
      gradient: colors[colorIndex].gradient,
      shadow: colors[colorIndex].shadow,
      hasMoon: config.hasMoon,
      moonSpeed: config.moonSpeed,
      isSpaceship: false,
      spaceshipSvg: '',
      spaceshipName: ''
    });
  }
  
  // Deterministically make the third planet (index 2) a spaceship if it doesn't have a moon
  if (!planets[2].hasMoon) {
    planets[2].isSpaceship = true;
    planets[2].spaceshipSvg = getSvgPath(spaceshipSvgs[2].svg); // Use third spaceship
    planets[2].spaceshipName = spaceshipSvgs[2].name;
    planets[2].size = 20;
  }
  
  return planets;
};

const planets = ref<Planet[]>([]);

// Error handling for SVG loading
const handleImageError = (event: Event) => {
  console.error('Failed to load spaceship SVG:', (event.target as HTMLImageElement)?.src);
};

const handleImageLoad = (event: Event) => {
  console.log('Successfully loaded spaceship SVG:', (event.target as HTMLImageElement)?.src);
};

// Only generate planets on client-side to avoid hydration mismatch
onMounted(() => {
  planets.value = generatePlanets();
});
</script>

<style scoped lang="scss">
.solar-system-loader {
  position: relative;
  width: 460px;
  height: 460px;
  margin: 0 auto;
}

.sun {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: radial-gradient(circle, #ffd700, #ff8c00);
  border-radius: 50%;
  box-shadow: 
    0 0 20px #ffd700,
    0 0 40px #ff8c00,
    0 0 60px #ff4500;
  animation: sun-pulse 2s ease-in-out infinite alternate;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-text {
  font-size: var(--fs-tiny);
  font-weight: bold;
  color: #000;
  text-shadow: 0 0 2px #fff;
  letter-spacing: 0.5px;
}

.orbit {
  position: absolute;
  top: 50%;
  left: 50%;
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: orbit-rotation linear infinite;
}

.planet {
  position: absolute;
  border-radius: 50%;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
}

.spaceship {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
}

.spaceship-image {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 0 4px #00bfff);
  transform: rotate(-90deg);
}

.moon {
  position: absolute;
  width: 6px;
  height: 6px;
  background: radial-gradient(circle, #ddd, #999);
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform-origin: 0 0;
  animation: moon-orbit 2s linear infinite;
}

@keyframes sun-pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    box-shadow: 
      0 0 20px #ffd700,
      0 0 40px #ff8c00,
      0 0 60px #ff4500;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 
      0 0 30px #ffd700,
      0 0 50px #ff8c00,
      0 0 70px #ff4500;
  }
}

@keyframes orbit-rotation {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes moon-orbit {
  0% {
    transform: translate(-50%, -50%) rotate(0deg) translateX(25px) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg) translateX(25px) rotate(-360deg);
  }
}

// Fallback spaceship shape if SVG doesn't load
.spaceship-fallback {
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, #00bfff, #0080ff);
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  filter: drop-shadow(0 0 4px #00bfff);
  transform: rotate(-90deg);
}
</style>
